(ns dashboard.events.calendars
  (:require
   [re-frame.core :as re-frame]
   [graphql-query.core :refer [graphql-query]]
   [dashboard.utils :refer [index-of ->json]]
   [dashboard.interceptors :refer [standard-interceptors]]
   [ajax.core :as ajax]))

;;------------------------
;; Calendars

(def calendar-fields
  [:id :name :nameJp :organizationId :spaceId :userId :color
   [:eventTags [:id :name :nameJp :color :description]]
   [:eventStatuses [:id :name :nameJp :color]]])

;; Create Calendar

(defn create-calendar-query
  [{:keys [calendar]}]
  {:operation {:operation/type :mutation
               :operation/name :addCalendar}
   :queries [[:addCalendar
              calendar
              calendar-fields]]})

(re-frame/reg-event-fx
 ::create-calendar
 standard-interceptors
 (fn [{:keys [db]} [{:keys [calendar]}]]
   {:graphql-request {:query (graphql-query (create-calendar-query
                                             {:calendar calendar}))
                      :on-success [::create-calendar-success]}}))

(re-frame/reg-event-fx
 ::create-calendar-success
 standard-interceptors
 (fn [{:keys [db]} [res]]
   (if (some? (:errors res))
     {:show-notification!
      {:title "Error Creating Calendar"
       :intent :intent/danger
       :duration 10000
       :message (clojure.string/join "\n" (map :message (:errors res)))}}
     {:show-notification!
      {:title "Calendar Created"
       :intent :intent/success
       :duration 10000
       :message (str "You successfuly created " (:name (get-in res [:data :addCalendar])))}
      :dispatch [::add-calendar (get-in res [:data :addCalendar])]})))

;; Update Calendar

(defn update-calendar-query
  [{:keys [calendar]}]
  {:operation {:operation/type :mutation
               :operation/name :updateCalendar}
   :queries [[:updateCalendar
              calendar
              calendar-fields]]})

(re-frame/reg-event-fx
 ::update-calendar
 standard-interceptors
 (fn [{:keys [db]} [{:keys [calendar]}]]
   {:graphql-request {:query (graphql-query (update-calendar-query
                                             {:calendar calendar}))
                      :on-success [::update-calendar-success]}}))

(re-frame/reg-event-fx
 ::update-calendar-success
 standard-interceptors
 (fn [{:keys [db]} [res]]
   (if (some? (:errors res))
     {:show-notification!
      {:title "Error Updating Calendar"
       :intent :intent/danger
       :duration 10000
       :message (clojure.string/join "\n" (map :message (:errors res)))}}
     {:show-notification!
      {:title "Calendar Updated"
       :intent :intent/success
       :duration 10000
       :message (str "You successfuly updated the calendar "
                     (:name (get-in res [:data :updateCalendar])))}
      :dispatch [::edit-calendar (get-in res [:data :updateCalendar])]})))

;; Create Calendar

(defn delete-calendar-query
  [{:keys [calendar]}]
  {:operation {:operation/type :mutation
               :operation/name :deleteCalendar}
   :queries [[:deleteCalendar
              {:id (:id calendar)}
              calendar-fields]]})

(re-frame/reg-event-fx
 ::delete-calendar
 standard-interceptors
 (fn [{:keys [db]} [{:keys [calendar]}]]
   {:graphql-request {:query (graphql-query (delete-calendar-query
                                             {:calendar calendar}))
                      :on-success [::delete-calendar-success]}}))

(re-frame/reg-event-fx
 ::delete-calendar-success
 standard-interceptors
 (fn [{:keys [db]} [res]]
   (if (some? (:errors res))
     {:show-notification!
      {:title "Error Deleting Calendar"
       :intent :intent/danger
       :duration 10000
       :message (clojure.string/join "\n" (map :message (:errors res)))}}
     {:show-notification!
      {:title "Calendar Deleted"
       :intent :intent/success
       :duration 10000
       :message (str "You successfuly deleted " (:name (get-in res [:data :deleteCalendar])))}
      :dispatch [::remove-calendar (get-in res [:data :deleteCalendar])]})))

;; Add a calendar to the list of events in the application db
(re-frame/reg-event-db
 ::add-calendar
 standard-interceptors
 (fn [db [calendar]]
   (update-in db [:calendars] conj calendar)))

;; Edit a calendar to the list of events in the application db
(re-frame/reg-event-db
 ::edit-calendar
 standard-interceptors
 (fn [db [calendar]]
   (let [calendar-idx (index-of (map :id (:calendars db)) (:id calendar))]
     (update-in db [:calendars calendar-idx] merge calendar))))

;; Remove a calendar from the list of events in the application db
(re-frame/reg-event-db
 ::remove-calendar
 standard-interceptors
 (fn [db [calendar]]
   (let [old-calendars (:calendars db)
         new-calendars (into [] (filter #(not= (:id %) (:id calendar)) old-calendars))]
     (assoc db :calendars new-calendars))))

;; List Calendars

(defn list-calendars-by-organization-query
  [{:keys [organization-id]}]
  {:operation {:operation/type :query
               :operation/name :allCalendarsByOrganization}
   :queries [[:allCalendarsByOrganization
              {:organizationId organization-id}
              {:calendars calendar-fields}]]})

(re-frame/reg-event-fx
 ::list-calendars-by-organization
 standard-interceptors
 (fn [{:keys [db]} [{:keys [organization-id]}]]
   (let [organization-id (or organization-id
                             (get-in db [:settings :organization :id]))]
     {:graphql-request {:query (graphql-query (list-calendars-by-organization-query
                                               {:organization-id organization-id}))
                        :on-success [::list-calendars-by-organization-success]}})))

(re-frame/reg-event-fx
 ::list-calendars-by-organization-success
 standard-interceptors
 (fn [{:keys [db]} [res]]
   {:db (assoc db :calendars (get-in res [:data :allCalendarsByOrganization :calendars]))}))

;;------------------------
;; Events

(def event-fields
  [:id :title :organizationId :calendarId :start :end :locationId :isAllDay :url
   :created :creatorId :statusId :tagIds :organizerId
   [:attendees [:userId :status]]])

;; Create Event

(defn create-event-query
  [{:keys [event]}]
  {:operation {:operation/type :mutation
               :operation/name :addEvent}
   :queries [[:addEvent
              event
              event-fields]]})

(re-frame/reg-event-fx
 ::create-event
 standard-interceptors
 (fn [{:keys [db]} [{:keys [event]}]]
   {:graphql-request {:query (graphql-query (create-event-query
                                             {:event event}))
                      :on-success [::create-event-success]}}))

(re-frame/reg-event-fx
 ::create-event-success
 standard-interceptors
 (fn [{:keys [db]} [res]]
   (if (some? (:errors res))
     {:show-notification!
      {:title "Error Creating Event"
       :intent :intent/danger
       :duration 10000
       :message (clojure.string/join "\n" (map :message (:errors res)))}}
     {:show-notification!
      {:title "Event Created"
       :intent :intent/success
       :duration 10000
       :message (str "You successfuly created " (:name (get-in res [:data :addEvent])))}
      :dispatch [::add-event (get-in res [:data :addEvent])]})))

;; Update Event

(defn update-event-query
  [{:keys [event]}]
  {:operation {:operation/type :mutation
               :operation/name :updateEvent}
   :queries [[:updateEvent
              (-> event
                  (dissoc :organizationId :created :floorplanConfig))
              event-fields]]})

(re-frame/reg-event-fx
 ::update-event
 standard-interceptors
 (fn [{:keys [db]} [{:keys [event]}]]
   {:graphql-request {:query (graphql-query (update-event-query {:event event}))
                      :on-success [::update-event-success]}}))

(re-frame/reg-event-fx
 ::update-event-success
 standard-interceptors
 (fn [{:keys [db]} [res]]
   (if (some? (:errors res))
     {:show-notification!
      {:title "Error Updating Event"
       :intent :intent/danger
       :duration 10000
       :message (clojure.string/join "\n" (map :message (:errors res)))}}
     {:show-notification!
      {:title "Event Updated"
       :intent :intent/success
       :duration 10000
       :message (str "You successfuly updated the event "
                     (:title (get-in res [:data :updateEvent])))}
      :dispatch [::edit-event (get-in res [:data :updateEvent])]})))

;; Delete Event

(defn delete-event-query
  [{:keys [event]}]
  {:operation {:operation/type :mutation
               :operation/name :deleteEvent}
   :queries [[:deleteEvent
              {:id (:id event)}
              event-fields]]})

(re-frame/reg-event-fx
 ::delete-event
 standard-interceptors
 (fn [{:keys [db]} [{:keys [event]}]]
   {:graphql-request {:query (graphql-query (delete-event-query {:event event}))
                      :on-success [::delete-event-success]}}))

(re-frame/reg-event-fx
 ::delete-event-success
 standard-interceptors
 (fn [{:keys [db]} [res]]
   (if (some? (:errors res))
     {:show-notification!
      {:title "Error Deleting Event"
       :intent :intent/danger
       :duration 10000
       :message (clojure.string/join "\n" (map :message (:errors res)))}}
     {:show-notification!
      {:title "Event Deleted"
       :intent :intent/success
       :duration 10000
       :message (str "You successfuly deleted " (:title (get-in res [:data :deleteEvent])))}
      :dispatch [::remove-event (get-in res [:data :deleteEvent])]})))

;; Add an event to the list of events in the application db
(re-frame/reg-event-db
 ::add-event
 standard-interceptors
 (fn [db [event]]
   (update-in db [:events] conj event)))

;; Edit an event to the list of events in the application db
(re-frame/reg-event-db
 ::edit-event
 standard-interceptors
 (fn [db [event]]
   (let [event-idx (index-of (map :id (:events db)) (:id event))]
     (update-in db [:events event-idx] merge event))))

;; Remove an event from the list of events in the application db
(re-frame/reg-event-db
 ::remove-event
 standard-interceptors
 (fn [db [event]]
   (let [old-events (:events db)
         new-events (into [] (filter #(not= (:id %) (:id event)) old-events))]
     (assoc db :events new-events))))

;; List Events

(defn list-events-by-organization-query
  [{:keys [organization-id]}]
  {:operation {:operation/type :query
               :operation/name :allEventsByOrganization}
   :queries [[:allEventsByOrganization
              {:organizationId organization-id}
              {:events event-fields}]]})

(re-frame/reg-event-fx
 ::list-events-by-organization
 standard-interceptors
 (fn [{:keys [db]} [{:keys [organization-id]}]]
   (let [organization-id (or organization-id
                             (get-in db [:settings :organization :id]))]
     {:graphql-request {:query (graphql-query (list-events-by-organization-query
                                               {:organization-id organization-id}))
                        :on-success [::list-events-by-organization-success]}})))

(re-frame/reg-event-fx
 ::list-events-by-organization-success
 standard-interceptors
 (fn [{:keys [db]} [res]]
   {:db (assoc db :events (get-in res [:data :allEventsByOrganization :events]))}))
